import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import Layout from '../components/Layout';
import AttendanceGate from '../components/AttendanceGate';
import { useAttendanceGate } from '../hooks/useAttendanceGate';
import { Input } from '../components/ui/input';
import { Button } from '../components/ui/button';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from '../components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import { 
  Receipt, 
  Plus, 
  Search, 
  Loader2, 
  Clock,
  CheckCircle,
  XCircle,
  Calculator,
  MapPin,
  Car,
  Trash2,
  RotateCcw,
  User
} from 'lucide-react';
import { toast } from 'sonner';
import { cn } from '../lib/utils';

const Expenses = () => {
  const { api, isAdmin, isSuperadmin } = useAuth();
  const navigate = useNavigate();
  const [expenses, setExpenses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [filterStatus, setFilterStatus] = useState('all');
  // Super admin only: days taken out of TADA, and the remove / restore dialog.
  // tadaAction is { mode: 'remove', expense } or { mode: 'restore', day }.
  const [excludedDays, setExcludedDays] = useState([]);
  const [tadaAction, setTadaAction] = useState(null);
  const [tadaReason, setTadaReason] = useState('');
  const [tadaSaving, setTadaSaving] = useState(false);

  useEffect(() => {
    fetchExpenses();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const fetchExpenses = async () => {
    try {
      const response = await api.get('/expenses');
      setExpenses(response.data);
      if (isSuperadmin) {
        const excluded = await api.get('/attendance/tada-excluded');
        setExcludedDays(excluded.data);
      }
    } catch (error) {
      console.error('Failed to fetch expenses:', error);
      toast.error('Failed to load expenses');
    } finally {
      setLoading(false);
    }
  };

  const openTadaAction = (action) => {
    setTadaReason('');
    setTadaAction(action);
  };

  const confirmTadaAction = async () => {
    setTadaSaving(true);
    try {
      const { data } = tadaAction.mode === 'remove'
        ? await api.post(`/expenses/${tadaAction.expense.id}/remove-tada`, { reason: tadaReason })
        : await api.post(`/attendance/${tadaAction.day.id}/tada`, { exclude: false, reason: tadaReason });
      toast.success(data.message);
      setTadaAction(null);
      fetchExpenses();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to change TADA for this day');
    } finally {
      setTadaSaving(false);
    }
  };

  const filteredExpenses = expenses.filter(expense => {
    const q = searchQuery.toLowerCase();
    const matchesSearch = expense.expense_number?.toLowerCase().includes(q) ||
      expense.expense_type?.toLowerCase().includes(q) ||
      expense.created_by_name?.toLowerCase().includes(q);
    const matchesStatus = filterStatus === 'all' || expense.status === filterStatus;
    return matchesSearch && matchesStatus;
  });

  const formatDate = (dateStr) => {
    const date = new Date(dateStr);
    return date.toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' });
  };

  if (loading) {
    return (
      <Layout>
        <div className="flex items-center justify-center h-[60vh]">
          <Loader2 className="w-8 h-8 animate-spin text-sky-600" />
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="space-y-4 fade-in" data-testid="expenses-page">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold text-slate-900">TADA & Expenses</h1>
            <p className="text-sm text-slate-500">Submit and track your expenses</p>
          </div>
          <Button
            onClick={() => navigate('/expenses/new')}
            data-testid="new-expense-btn"
            className="h-10 px-4 rounded-xl bg-amber-600 hover:bg-amber-700 text-white shadow-lg shadow-amber-200"
          >
            <Plus className="w-4 h-4 mr-2" />
            Add Expense
          </Button>
        </div>

        {/* Filters */}
        <div className="flex gap-3">
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
            <Input
              placeholder="Search expenses..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="pl-10 h-11 rounded-xl"
              data-testid="search-expenses"
            />
          </div>
          <Select value={filterStatus} onValueChange={setFilterStatus}>
            <SelectTrigger className="w-[140px] h-11 rounded-xl" data-testid="filter-status">
              <SelectValue placeholder="Status" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Status</SelectItem>
              <SelectItem value="pending">Pending</SelectItem>
              <SelectItem value="approved">Approved</SelectItem>
              <SelectItem value="rejected">Rejected</SelectItem>
            </SelectContent>
          </Select>
        </div>

        {/* Super admin: days taken out of TADA, so they can be put back */}
        {isSuperadmin && excludedDays.length > 0 && (
          <div className="bg-white rounded-2xl border border-violet-100 p-4 space-y-2"
               data-testid="tada-excluded-panel">
            <h2 className="text-sm font-semibold text-slate-700">
              Removed TADA days ({excludedDays.length})
            </h2>
            {excludedDays.map((day) => (
              <div key={day.id}
                   className="flex items-center justify-between gap-2 p-3 rounded-xl border border-slate-100"
                   data-testid={`tada-excluded-${day.id}`}>
                <div className="min-w-0">
                  <p className="font-medium text-slate-900 truncate">
                    {day.user_name} · {formatDate(day.date)}
                  </p>
                  <p className="text-xs text-slate-500 truncate">
                    {(day.tada_excluded_claim || []).join(', ') || 'No claim'} removed
                    {day.tada_excluded_by ? ` by ${day.tada_excluded_by}` : ''}
                    {day.tada_excluded_reason ? ` - ${day.tada_excluded_reason}` : ''}
                  </p>
                </div>
                <Button size="sm" variant="outline"
                        className="shrink-0 rounded-lg text-emerald-700 border-emerald-200 hover:bg-emerald-50"
                        onClick={() => openTadaAction({ mode: 'restore', day })}
                        data-testid={`restore-tada-${day.id}`}>
                  <RotateCcw className="w-3 h-3 mr-1" /> Restore
                </Button>
              </div>
            ))}
          </div>
        )}

        {/* Expenses List */}
        <div className="space-y-3">
          {filteredExpenses.length === 0 ? (
            <div className="text-center py-12 bg-slate-50 rounded-2xl">
              <Receipt className="w-12 h-12 text-slate-300 mx-auto mb-3" />
              <p className="text-slate-500">No expenses found</p>
            </div>
          ) : (
            filteredExpenses.map((expense) => (
              <div
                key={expense.id}
                className="bg-white rounded-2xl border border-slate-100 p-4 hover:shadow-md transition-all card-hover"
                data-testid={`expense-card-${expense.id}`}
              >
                <div className="flex items-start justify-between">
                  <div className="flex items-start gap-3">
                    <div className={cn(
                      "w-10 h-10 rounded-xl flex items-center justify-center",
                      expense.expense_type === 'TADA' ? "bg-amber-100" : "bg-blue-100"
                    )}>
                      {expense.expense_type === 'TADA' ? (
                        <Car className="w-5 h-5 text-amber-600" />
                      ) : (
                        <Receipt className="w-5 h-5 text-blue-600" />
                      )}
                    </div>
                    <div>
                      <h3 className="font-medium text-slate-900">{expense.expense_number}</h3>
                      {isAdmin && expense.created_by_name && (
                        <p className="text-sm text-slate-700 flex items-center gap-1">
                          <User className="w-3 h-3" /> {expense.created_by_name}
                        </p>
                      )}
                      <p className="text-sm text-slate-500">
                        {expense.expense_type}
                        {expense.travel_type && ` • ${expense.travel_type}`}
                        {expense.distance_km && ` • ${expense.distance_km} km`}
                      </p>
                      <div className="flex items-center gap-3 mt-2 text-xs text-slate-400">
                        <span className="flex items-center gap-1">
                          <Clock className="w-3 h-3" />
                          {formatDate(expense.date)}
                        </span>
                        {expense.from_location && expense.to_location && (
                          <span className="flex items-center gap-1">
                            <MapPin className="w-3 h-3" />
                            {expense.from_location} → {expense.to_location}
                          </span>
                        )}
                        {expense.is_auto_calculated && (
                          <span className="flex items-center gap-1 text-emerald-500">
                            <Calculator className="w-3 h-3" />
                            Auto-calculated
                          </span>
                        )}
                      </div>
                    </div>
                  </div>
                  <div className="text-right">
                    <p className="text-lg font-bold text-emerald-600">₹{expense.amount?.toLocaleString()}</p>
                    <span className={cn(
                      "px-2.5 py-1 rounded-full text-xs font-medium",
                      expense.status === 'approved' && "bg-emerald-100 text-emerald-700",
                      expense.status === 'rejected' && "bg-red-100 text-red-700",
                      expense.status === 'pending' && "bg-amber-100 text-amber-700"
                    )}>
                      {expense.status === 'approved' && <CheckCircle className="w-3 h-3 inline mr-1" />}
                      {expense.status === 'rejected' && <XCircle className="w-3 h-3 inline mr-1" />}
                      {expense.status}
                    </span>
                    {isSuperadmin && expense.expense_type === 'TADA' && expense.is_auto_claimed && (
                      <div className="mt-2">
                        <Button size="sm" variant="outline"
                                className="h-8 rounded-lg text-red-600 border-red-200 hover:bg-red-50"
                                onClick={() => openTadaAction({ mode: 'remove', expense })}
                                data-testid={`remove-tada-${expense.id}`}>
                          <Trash2 className="w-3 h-3 mr-1" /> Remove TADA
                        </Button>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            ))
          )}
        </div>

        <Dialog open={Boolean(tadaAction)} onOpenChange={(open) => !open && setTadaAction(null)}>
          <DialogContent className="sm:max-w-md">
            <DialogHeader>
              <DialogTitle>
                {tadaAction?.mode === 'remove'
                  ? `Remove TADA for ${tadaAction.expense.created_by_name || 'this user'} on ${formatDate(tadaAction.expense.date)}?`
                  : `Restore TADA for ${tadaAction?.day.user_name} on ${tadaAction ? formatDate(tadaAction.day.date) : ''}?`}
              </DialogTitle>
              <DialogDescription>
                {tadaAction?.mode === 'remove'
                  ? `Claim ${tadaAction.expense.expense_number} (₹${tadaAction.expense.amount?.toLocaleString()}) is deleted and the day is taken out of TADA, so it is not raised again. The punches stay as they are.`
                  : 'The day goes back under TADA and its automatic claim is raised again from the journey.'}
              </DialogDescription>
            </DialogHeader>
            <div className="space-y-2">
              <Label>Reason (optional)</Label>
              <Textarea
                value={tadaReason}
                onChange={(e) => setTadaReason(e.target.value)}
                placeholder={tadaAction?.mode === 'remove' ? 'e.g. Sunday - no field work' : 'e.g. removed by mistake'}
                data-testid="tada-reason"
              />
            </div>
            <DialogFooter className="gap-2">
              <Button variant="outline" onClick={() => setTadaAction(null)} disabled={tadaSaving}>
                Cancel
              </Button>
              <Button className={tadaAction?.mode === 'remove'
                        ? 'bg-red-600 hover:bg-red-700 text-white'
                        : 'bg-emerald-600 hover:bg-emerald-700 text-white'}
                      onClick={confirmTadaAction} disabled={tadaSaving}
                      data-testid="tada-confirm">
                {tadaSaving && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
                {tadaAction?.mode === 'remove' ? 'Remove TADA' : 'Restore TADA'}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    </Layout>
  );
};

/**
 * TADA entry.
 *
 * Headquarter and Ex-Station are fully automated: the travel type, distance,
 * reporting times and mode (always Bike) come from that day's punch in ->
 * punch out journey. Outstation is the only manually entered travel type,
 * and "Ex-Station without bike" no longer exists — it is claimed as Outstation.
 */
export const NewExpense = () => {
  const { api } = useAuth();
  const gate = useAttendanceGate();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [autofilling, setAutofilling] = useState(false);
  const [autofill, setAutofill] = useState(null);
  const [formData, setFormData] = useState({
    expense_type: '',
    date: new Date().toISOString().split('T')[0],
    travel_type: '',
    from_location: '',
    to_location: '',
    first_reporting_time: '',
    last_reporting_time: '',
    mode_of_travel: '',
    distance_km: '',
    expense_category: '',
    amount: ''
  });

  const isAutomated = formData.expense_type === 'TADA' && formData.travel_type !== 'Out Station';
  const isOutStation = formData.expense_type === 'TADA' && formData.travel_type === 'Out Station';

  // The Outstation block, exactly as the PSM form collects it: a fare against
  // each mode, the class of city, whether the company booked the hotel, and
  // whether incidentals apply.
  const [outStation, setOutStation] = useState({
    fares: { Bike: '', Bus: '', Train: '', Taxi: '' },
    city_type: '',
    hotel_co_arrangement: '',
    hotel_amount: '',
    incidental: '',
  });
  const [entitlement, setEntitlement] = useState(null);
  // Which "Other" categories this user may claim. Mobile is an entitlement HR
  // sets per employee, so the server decides and the form just renders it.
  const [categories, setCategories] = useState([]);

  useEffect(() => {
    api.get('/expenses/categories')
      .then((res) => setCategories(res.data.categories || []))
      .catch(() => setCategories([]));
  }, [api]);

  // What the card allows this user, so the form can show the ceiling rather than
  // letting someone fill in a number that will be silently capped on save.
  useEffect(() => {
    if (!isOutStation || entitlement) return;
    api.get('/expenses/out-station-entitlement')
      .then((res) => setEntitlement(res.data))
      .catch(() => { /* the server still prices it; the hint is a convenience */ });
  }, [isOutStation, entitlement, api]);

  const outStationCeiling = entitlement?.lodging?.[outStation.city_type] ?? null;

  const outStationTotal = useMemo(() => {
    const fareTotal = Object.values(outStation.fares)
      .reduce((sum, v) => sum + (parseFloat(v) || 0), 0);
    let lodging = 0;
    if (outStationCeiling) {
      if (outStation.hotel_co_arrangement === 'Yes') {
        lodging = Math.min(parseFloat(outStation.hotel_amount) || 0, outStationCeiling);
      } else if (outStation.hotel_co_arrangement === 'No') {
        lodging = outStationCeiling * 0.5;
      }
    }
    const incidental = outStation.incidental === 'Yes' ? (entitlement?.incidental || 0) : 0;
    return {
      fareTotal: Math.round(fareTotal * 100) / 100,
      lodging: Math.round(lodging * 100) / 100,
      incidental,
      total: Math.round((fareTotal + lodging + incidental) * 100) / 100,
    };
  }, [outStation, outStationCeiling, entitlement]);


  // KEYNOTE 15/16: expense entry needs an open day. TADA is the one exception -
  // it is calculated from the completed punch in -> punch out journey, so it can
  // only be claimed once the day is already closed.
  const blockedByAttendance = !gate.allowed && formData.expense_type && formData.expense_type !== 'TADA';

  // Pull the day's journey whenever TADA + a date are in play.
  useEffect(() => {
    if (formData.expense_type !== 'TADA' || !formData.date) {
      setAutofill(null);
      return;
    }
    let cancelled = false;
    setAutofilling(true);
    api.get(`/expenses/tada-autofill?date=${formData.date}`)
      .then(({ data }) => {
        if (cancelled) return;
        setAutofill(data);
        if (data.available) {
          setFormData((prev) => ({
            ...prev,
            travel_type: prev.travel_type === 'Out Station' ? prev.travel_type : data.travel_type,
            from_location: prev.from_location || data.from_location,
            to_location: prev.to_location || data.to_location,
            first_reporting_time: data.first_reporting_time,
            last_reporting_time: data.last_reporting_time,
            mode_of_travel: data.mode_of_travel,
            distance_km: String(data.distance_km),
            amount: data.amount != null ? String(data.amount) : prev.amount
          }));
        }
      })
      .catch(() => !cancelled && setAutofill(null))
      .finally(() => !cancelled && setAutofilling(false));
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [formData.expense_type, formData.date]);

  // Switching between automated and Outstation swaps which fields apply.
  const handleTravelTypeChange = (value) => {
    if (value === 'Out Station') {
      setFormData((prev) => ({
        ...prev,
        travel_type: value,
        mode_of_travel: '',
        distance_km: '',
        amount: ''
      }));
    } else if (autofill?.available) {
      setFormData((prev) => ({
        ...prev,
        travel_type: autofill.travel_type,
        mode_of_travel: autofill.mode_of_travel,
        distance_km: String(autofill.distance_km),
        amount: autofill.amount != null ? String(autofill.amount) : ''
      }));
    } else {
      setFormData((prev) => ({ ...prev, travel_type: value }));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.expense_type) {
      toast.error('Select an expense type');
      return;
    }

    if (formData.expense_type === 'TADA') {
      if (!formData.travel_type) {
        toast.error('Select a travel type');
        return;
      }
      if (!formData.from_location || !formData.to_location) {
        toast.error('From and To are required');
        return;
      }
      if (isAutomated && !autofill?.available) {
        toast.error(autofill?.reason || 'Headquarter and Ex-Station TADA need a completed punch in / punch out');
        return;
      }
      // Outstation is not a single typed amount any more - it is fares, a class
      // of city, lodging and incidentals, and the total comes from those.
      if (isOutStation) {
        if (!outStation.city_type) {
          toast.error('Select the city type');
          return;
        }
        if (!outStation.hotel_co_arrangement) {
          toast.error('Say whether the hotel was a co-arrangement');
          return;
        }
        if (outStation.hotel_co_arrangement === 'Yes' && !outStation.hotel_amount) {
          toast.error('Enter the hotel amount');
          return;
        }
        if (!outStationTotal.total) {
          toast.error('Enter the Outstation fares, lodging or incidentals');
          return;
        }
      }
    } else if (!formData.amount) {
      toast.error('Enter the amount');
      return;
    }

    if (formData.expense_type === 'Other' && !formData.expense_category) {
      toast.error('Please select expense category');
      return;
    }

    setLoading(true);
    try {
      await api.post('/expenses', {
        ...formData,
        amount: isOutStation ? outStationTotal.total : (parseFloat(formData.amount) || 0),
        ...(isOutStation ? {
          out_station: {
            fares: Object.fromEntries(Object.entries(outStation.fares)
              .map(([mode, v]) => [mode, parseFloat(v) || 0])),
            city_type: outStation.city_type,
            hotel_co_arrangement: outStation.hotel_co_arrangement,
            hotel_amount: parseFloat(outStation.hotel_amount) || 0,
            incidental: outStation.incidental,
          },
        } : {}),
        distance_km: formData.distance_km ? parseFloat(formData.distance_km) : null,
        is_auto_calculated: isAutomated
      });
      toast.success('Expense submitted successfully!');
      navigate('/expenses');
    } catch (error) {
      console.error('Failed to create expense:', error);
      toast.error(error.response?.data?.detail || 'Failed to submit expense');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Layout>
      <div className="max-w-2xl mx-auto space-y-6 fade-in" data-testid="new-expense-page">
        {/* Header */}
        <div>
          <h1 className="text-2xl font-bold text-slate-900">Add Expense</h1>
          <p className="text-sm text-slate-500">Submit a new TADA or expense claim</p>
        </div>

        <form onSubmit={handleSubmit} className="bg-white rounded-2xl border border-slate-200 p-5 space-y-5">
          <div className="space-y-2">
            <Label>Expense Type *</Label>
            <Select
              value={formData.expense_type}
              onValueChange={(value) => {
                setFormData({ ...formData, expense_type: value, travel_type: '', expense_category: '', amount: '' });
                setCalculatedInfo(null);
              }}
            >
              <SelectTrigger className="h-12 rounded-xl" data-testid="expense-type-select">
                <SelectValue placeholder="Select type" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="TADA">TADA (Travel Allowance)</SelectItem>
                <SelectItem value="Other">Other Expense</SelectItem>
              </SelectContent>
            </Select>
          </div>

          <div className="space-y-2">
            <Label>Date *</Label>
            <Input
              type="date"
              value={formData.date}
              onChange={(e) => setFormData({ ...formData, date: e.target.value })}
              className="h-12 rounded-xl"
              data-testid="expense-date"
            />
          </div>

          {/* TADA specific fields */}
          {formData.expense_type === 'TADA' && (
            <>
              {/* Journey status for the selected date */}
              {autofilling ? (
                <div className="flex items-center gap-2 p-3 bg-slate-50 rounded-xl text-sm text-slate-600">
                  <Loader2 className="w-4 h-4 animate-spin" />
                  Reading the journey for this date...
                </div>
              ) : autofill && !autofill.available ? (
                <div className="p-3 bg-amber-50 border border-amber-200 rounded-xl text-sm text-amber-700">
                  {autofill.reason} Only Outstation can be claimed manually.
                </div>
              ) : autofill?.available ? (
                <div className="p-4 bg-emerald-50 border border-emerald-200 rounded-xl space-y-1 text-sm">
                  <p className="font-medium text-emerald-800 flex items-center gap-2">
                    <Calculator className="w-4 h-4" />
                    Journey auto-detected
                  </p>
                  <div className="grid grid-cols-2 gap-x-3 gap-y-1 text-emerald-700">
                    <span>Distance travelled</span>
                    <span className="font-medium">{autofill.travelled_km} km</span>
                    <span>Claimable distance</span>
                    <span className="font-medium">{autofill.distance_km} km</span>
                    <span>Travel type ({autofill.threshold_km} km rule)</span>
                    <span className="font-medium">{autofill.travel_type}</span>
                    <span>Approved visits</span>
                    <span className="font-medium">
                      {autofill.visits_approved} of {autofill.visits_total}
                    </span>
                    <span>Designation</span>
                    <span className="font-medium">{autofill.designation}</span>
                  </div>
                  {autofill.pending_note && (
                    <p className="text-amber-700 pt-2 border-t border-emerald-200">{autofill.pending_note}</p>
                  )}
                  {autofill.rate_error && (
                    <p className="text-amber-700 pt-2 border-t border-emerald-200">{autofill.rate_error}</p>
                  )}
                </div>
              ) : null}

              <div className="space-y-2">
                <Label>Travel Type *</Label>
                <Select value={formData.travel_type} onValueChange={handleTravelTypeChange}>
                  <SelectTrigger className="h-12 rounded-xl" data-testid="travel-type-select">
                    <SelectValue placeholder="Select travel type" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="HQ">Headquarter (auto)</SelectItem>
                    <SelectItem value="Ex Station">Ex Station (auto)</SelectItem>
                    <SelectItem value="Out Station">Outstation (manual)</SelectItem>
                  </SelectContent>
                </Select>
                <p className="text-xs text-slate-500">
                  Headquarter and Ex-Station are decided by the {autofill?.threshold_km ?? 30} km rule and
                  always travelled by bike. An Ex-Station trip made without a bike is claimed as Outstation.
                </p>
              </div>

              {isOutStation && (
                <div className="rounded-2xl border border-sky-200 bg-sky-50/60 p-4 space-y-4"
                     data-testid="out-station-block">
                  <div className="flex items-baseline justify-between">
                    <h4 className="font-semibold text-slate-900">Out Station</h4>
                    {entitlement?.card_row && (
                      <span className="text-xs text-slate-500">{entitlement.card_row}</span>
                    )}
                  </div>

                  {/* A fare against each mode, at actuals. */}
                  <div className="space-y-2">
                    <Label className="text-sm">Mode of travel &amp; amount</Label>
                    {(entitlement?.modes || ['Bike', 'Bus', 'Train', 'Taxi']).map((mode) => (
                      <div key={mode} className="grid grid-cols-2 gap-3">
                        <Input value={mode} readOnly className="h-11 rounded-xl bg-slate-100" />
                        <Input
                          type="number"
                          min="0"
                          value={outStation.fares[mode] ?? ''}
                          onChange={(e) => setOutStation((prev) => ({
                            ...prev, fares: { ...prev.fares, [mode]: e.target.value },
                          }))}
                          placeholder="Amount"
                          className="h-11 rounded-xl"
                          data-testid={`out-station-fare-${mode.toLowerCase()}`}
                        />
                      </div>
                    ))}
                  </div>

                  <div className="space-y-2">
                    <Label className="text-sm">City Type *</Label>
                    <Select
                      value={outStation.city_type}
                      onValueChange={(value) => setOutStation((prev) => ({ ...prev, city_type: value }))}
                    >
                      <SelectTrigger className="h-11 rounded-xl" data-testid="out-station-city-type">
                        <SelectValue placeholder="Select city type" />
                      </SelectTrigger>
                      <SelectContent>
                        {(entitlement?.city_classes || ['Class A', 'Class B', 'Class C']).map((c) => (
                          <SelectItem key={c} value={c}>
                            {c}{entitlement?.lodging?.[c] ? ` — up to ₹${entitlement.lodging[c]}` : ''}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>

                  <div className="space-y-2">
                    <Label className="text-sm">Co-Arrangement (Hotel Bill) *</Label>
                    <Select
                      value={outStation.hotel_co_arrangement}
                      onValueChange={(value) => setOutStation((prev) => ({
                        ...prev, hotel_co_arrangement: value,
                        hotel_amount: value === 'Yes' ? prev.hotel_amount : '',
                      }))}
                    >
                      <SelectTrigger className="h-11 rounded-xl" data-testid="out-station-hotel">
                        <SelectValue placeholder="Select co-arrangement" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="Yes">Yes</SelectItem>
                        <SelectItem value="No">No</SelectItem>
                      </SelectContent>
                    </Select>
                    {outStation.hotel_co_arrangement === 'No' && outStationCeiling && (
                      <p className="text-xs text-slate-500">
                        Own arrangement pays half the eligibility — ₹{outStationCeiling / 2}.
                      </p>
                    )}
                  </div>

                  {/* Revealed only for a co-arrangement, as on the PSM form. */}
                  {outStation.hotel_co_arrangement === 'Yes' && (
                    <div className="space-y-2">
                      <Label className="text-sm">Hotel Amount *</Label>
                      <Input
                        type="number"
                        min="0"
                        value={outStation.hotel_amount}
                        onChange={(e) => setOutStation((prev) => ({ ...prev, hotel_amount: e.target.value }))}
                        placeholder="What the hotel cost"
                        className="h-11 rounded-xl"
                        data-testid="out-station-hotel-amount"
                      />
                      {outStationCeiling
                        && parseFloat(outStation.hotel_amount) > outStationCeiling && (
                        <p className="text-xs text-amber-600">
                          Above the ₹{outStationCeiling} ceiling for {outStation.city_type} — it will be
                          reimbursed at ₹{outStationCeiling}.
                        </p>
                      )}
                    </div>
                  )}

                  <div className="space-y-2">
                    <Label className="text-sm">Incidental Expenses</Label>
                    <Select
                      value={outStation.incidental}
                      onValueChange={(value) => setOutStation((prev) => ({ ...prev, incidental: value }))}
                    >
                      <SelectTrigger className="h-11 rounded-xl" data-testid="out-station-incidental">
                        <SelectValue placeholder="Select" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="Yes">
                          Yes{entitlement?.incidental ? ` — ₹${entitlement.incidental}` : ''}
                        </SelectItem>
                        <SelectItem value="No">No</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>

                  <div className="rounded-xl bg-white border border-slate-200 p-3 text-sm space-y-1">
                    <div className="flex justify-between"><span className="text-slate-500">Fares</span>
                      <span>₹{outStationTotal.fareTotal}</span></div>
                    <div className="flex justify-between"><span className="text-slate-500">Lodging</span>
                      <span>₹{outStationTotal.lodging}</span></div>
                    <div className="flex justify-between"><span className="text-slate-500">Incidentals</span>
                      <span>₹{outStationTotal.incidental}</span></div>
                    <div className="flex justify-between pt-1 border-t border-slate-100 font-semibold">
                      <span>Total</span><span data-testid="out-station-total">₹{outStationTotal.total}</span>
                    </div>
                    <p className="text-xs text-slate-400 pt-1">
                      The server re-prices this from the reimbursement card on save.
                    </p>
                  </div>
                </div>
              )}

              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-2">
                  <Label>From *</Label>
                  <Input
                    value={formData.from_location}
                    onChange={(e) => setFormData({ ...formData, from_location: e.target.value })}
                    placeholder="Starting point"
                    className="h-12 rounded-xl"
                    data-testid="from-location"
                  />
                </div>
                <div className="space-y-2">
                  <Label>To *</Label>
                  <Input
                    value={formData.to_location}
                    onChange={(e) => setFormData({ ...formData, to_location: e.target.value })}
                    placeholder="Destination"
                    className="h-12 rounded-xl"
                    data-testid="to-location"
                  />
                </div>
              </div>

              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-2">
                  <Label>1st Reporting Time</Label>
                  <Input
                    type="time"
                    value={formData.first_reporting_time}
                    onChange={(e) => setFormData({ ...formData, first_reporting_time: e.target.value })}
                    className="h-12 rounded-xl"
                    data-testid="first-reporting-time"
                  />
                </div>
                <div className="space-y-2">
                  <Label>Last Reporting Time</Label>
                  <Input
                    type="time"
                    value={formData.last_reporting_time}
                    onChange={(e) => setFormData({ ...formData, last_reporting_time: e.target.value })}
                    className="h-12 rounded-xl"
                    data-testid="last-reporting-time"
                  />
                </div>
              </div>

              <div className="grid grid-cols-2 gap-3">
                {/* Outstation states its modes above, one fare per mode, so asking
                    again here only invited a third, contradictory answer. For
                    Headquarter and Ex-Station it stays: it shows the Bike the
                    journey was priced as (KEYNOTE 20), read only. */}
                {!isOutStation && (
                  <div className="space-y-2">
                    <Label>Mode / Class</Label>
                    <Input
                      value={isAutomated ? 'Bike' : formData.mode_of_travel}
                      onChange={(e) => setFormData({ ...formData, mode_of_travel: e.target.value })}
                      placeholder={isAutomated ? 'Bike' : 'Bus / Train / Car'}
                      disabled={isAutomated}
                      className="h-12 rounded-xl disabled:bg-slate-100"
                      data-testid="mode-of-travel"
                    />
                  </div>
                )}
                {/* Outstation is priced by class of city, not by distance, so the
                    KM box has nothing to act on there. */}
                {!isOutStation && (
                  <div className="space-y-2">
                    <Label>Distance (KM)</Label>
                    <Input
                      type="number"
                      value={formData.distance_km}
                      onChange={(e) => setFormData({ ...formData, distance_km: e.target.value })}
                      placeholder={isAutomated ? 'From journey' : 'Enter KM'}
                      disabled={isAutomated}
                      className="h-12 rounded-xl disabled:bg-slate-100"
                      data-testid="distance-km"
                    />
                  </div>
                )}
              </div>

              {/* Rate breakdown */}
              {isAutomated && autofill?.available && autofill.amount != null && (
                <div className="p-4 bg-slate-50 rounded-xl space-y-2 text-sm">
                  <p className="font-medium text-slate-700">TADA calculation</p>
                  <div className="grid grid-cols-2 gap-2 text-slate-600">
                    <span>Rate per KM</span>
                    <span className="font-medium">₹{autofill.per_km_rate}</span>
                    <span>Daily allowance</span>
                    <span className="font-medium">₹{autofill.daily_allowance}</span>
                    <span>Distance</span>
                    <span className="font-medium">{autofill.distance_km} km</span>
                    <span className="text-emerald-700 font-semibold">Total</span>
                    <span className="text-emerald-700 font-bold">₹{autofill.amount}</span>
                  </div>
                </div>
              )}
            </>
          )}

          {/* Other expense specific fields */}
          {formData.expense_type === 'Other' && (
            <div className="space-y-2">
              <Label>Expense Category *</Label>
              <Select
                value={formData.expense_category}
                onValueChange={(value) => setFormData({ ...formData, expense_category: value })}
              >
                <SelectTrigger className="h-12 rounded-xl" data-testid="expense-category-select">
                  <SelectValue placeholder="Select category" />
                </SelectTrigger>
                <SelectContent>
                  {categories.map((c) => (
                    <SelectItem key={c.value} value={c.value}>{c.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          )}

          <div className="space-y-2">
            <Label>Amount (₹) {isAutomated || isOutStation ? '' : '*'}</Label>
            <Input
              type="number"
              value={isOutStation ? outStationTotal.total : formData.amount}
              onChange={(e) => setFormData({ ...formData, amount: e.target.value })}
              placeholder={isAutomated ? 'Calculated automatically' : 'Enter amount'}
              className="h-12 rounded-xl disabled:bg-slate-100"
              disabled={isAutomated || isOutStation}
              data-testid="expense-amount"
            />
            {isOutStation && (
              <p className="text-xs text-slate-500">
                Totalled from the Outstation block above — fares, lodging and incidentals —
                and re-priced against the reimbursement card on save.
              </p>
            )}
            {isAutomated && (
              <p className="text-xs text-slate-500">
                Headquarter and Ex-Station amounts are calculated from the journey and the
                admin's rate card — they cannot be typed in.
              </p>
            )}
          </div>

          {blockedByAttendance && (
            <div className="bg-amber-50 border border-amber-200 rounded-xl p-4 flex items-start gap-3">
              <Clock className="w-5 h-5 text-amber-600 shrink-0 mt-0.5" />
              <div>
                <p className="font-medium text-amber-800">
                  {gate.reason === 'punched-out' ? 'Your day is closed' : 'Punch in to start your day'}
                </p>
                <p className="text-xs text-amber-700 mt-0.5">
                  {gate.reason === 'punched-out'
                    ? `You punched out${gate.punchOutTime ? ` at ${gate.punchOutTime}` : ''}, so this expense can no longer be recorded today.`
                    : 'You need to punch in before you can record this expense.'}
                  {' '}TADA claims are the exception — they are calculated from the completed day.
                </p>
              </div>
            </div>
          )}

          <div className="flex gap-3 pt-2">
            <Button
              type="button"
              variant="outline"
              onClick={() => navigate('/expenses')}
              className="flex-1 h-12 rounded-xl"
            >
              Cancel
            </Button>
            <Button
              type="submit"
              disabled={loading || blockedByAttendance}
              className="flex-1 h-12 rounded-xl bg-amber-600 hover:bg-amber-700 text-white"
              data-testid="submit-expense"
            >
              {loading ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Submitting...
                </>
              ) : (
                'Submit Expense'
              )}
            </Button>
          </div>
        </form>
      </div>
    </Layout>
  );
};

export default Expenses;
